import * as React from 'react';
import {
  FieldTimeOutlined,
  SearchOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { Segmented, Spin } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RoundInput } from 'components/Custom/inputs';
import { ScopeTable } from 'components/Custom/table';
import { User } from 'store/auth/models';
import { getTransactionHistory } from 'store/organisation/api';
import { getUserMembers } from 'store/user/selectors';
import {
  formatTransactionAmountDisplay,
  formatTransactionQuantityDisplay,
} from 'utils/numberFormat';
import {
  getEntityTransactionColumnTitle,
  getSourceTypeDisplayLabel,
  NUMERIC_COLUMN_KEYS,
} from './constants';
import { formatJournalModifiedDateForDisplay } from './entity-transaction-helpers';
import {
  HistoryChangeArrow,
  HistoryChangeFrom,
  HistoryChangeList,
  HistoryChangeRow,
  HistoryChangeTo,
  HistoryEmptyState,
  HistoryEventBadge,
  HistoryMetaLine,
  HistorySearchRow,
  HistorySummaryFacts,
  HistorySummaryFact,
  HistoryTableWrap,
  HistoryTimeline,
  HistoryTimelineCard,
  HistoryTimelineHeader,
  HistoryTimelineItem,
  HistoryTimelineMarker,
  HistoryTimelineRail,
  HistoryToolbar,
  HistoryViewToggle,
} from './styles';

type HistoryRow = Record<string, unknown> & {
  key: string;
};

type HistoryEventKind =
  | 'original'
  | 'reversal'
  | 'updated'
  | 'deleted'
  | 'current';

type FieldChange = {
  key: string;
  label: string;
  from: string;
  to: string;
};

type TimelineEntry = {
  row: HistoryRow;
  data: Record<string, unknown>;
  kind: HistoryEventKind;
  isCurrent: boolean;
  step: number;
  eventDateLabel: string | null;
  actorName: string | null;
  changes: FieldChange[];
};

const HISTORY_COMPARE_FIELDS = [
  'uploadDate',
  'transactionDate',
  'journalDate',
  'dueDate',
  'sourceType',
  'transactionNumber',
  'currency',
  'exchangeRate',
  'discountAmount',
  'gross',
  'net',
  'tax',
  'taxRate',
  'taxName',
  'description',
  'accountCode',
  'accountName',
  'itemId',
  'itemName',
  'contactName',
  'reference',
  'netAmountConverted',
  'taxAmountConverted',
  'grossAmountConverted',
] as const;

const HISTORY_SUMMARY_FIELDS = [
  'transactionDate',
  'journalDate',
  'sourceType',
  'transactionNumber',
  'currency',
  'gross',
  'net',
  'tax',
  'netAmountConverted',
  'taxAmountConverted',
  'grossAmountConverted',
] as const;

function extractHistoryList(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  if (!raw || typeof raw !== 'object') return [];
  const obj = raw as Record<string, unknown>;
  if (Array.isArray(obj.history)) return obj.history as Record<string, unknown>[];
  if (Array.isArray(obj.activities))
    return obj.activities as Record<string, unknown>[];
  if (Array.isArray(obj.transactions))
    return obj.transactions as Record<string, unknown>[];
  if (Array.isArray(obj.content))
    return obj.content as Record<string, unknown>[];
  if (Array.isArray(obj.data)) return obj.data as Record<string, unknown>[];
  if (obj.data && typeof obj.data === 'object') {
    return extractHistoryList(obj.data);
  }
  return [];
}

function pickHistoryField(
  row: Record<string, unknown>,
  keys: string[]
): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value != null && String(value).trim() !== '') return value;
  }
  return null;
}

function getHistoryRowData(record: HistoryRow): Record<string, unknown> {
  return (record.__transactionRow ?? record) as Record<string, unknown>;
}

function isDeletedHistoryRow(row: Record<string, unknown>): boolean {
  return getHistoryReversalType(row) === 'DELETED';
}

function getHistoryReversalType(row: Record<string, unknown>): string {
  return String(row.reversalType ?? 'NONE').toUpperCase();
}

function getRowUuid(row: Record<string, unknown>): string {
  return String(row.uuid ?? '').trim();
}

function getLinkedTransactionId(row: Record<string, unknown>): string {
  const linked = row.linkedTransactionId;
  if (linked == null) return '';
  return String(linked).trim();
}

function getEditedDateRaw(row: Record<string, unknown>): unknown {
  return pickHistoryField(row, [
    'editedDate',
    'edited_date',
    'journalModifiedDate',
    'journal_modified_date',
  ]);
}

function getDeletedDateRaw(row: Record<string, unknown>): unknown {
  return pickHistoryField(row, [
    'deletedDate',
    'deleted_date',
    'journalModifiedDate',
    'journal_modified_date',
  ]);
}

function getSortDateRaw(row: Record<string, unknown>): unknown {
  return (
    pickHistoryField(row, [
      'editedDate',
      'edited_date',
      'deletedDate',
      'deleted_date',
      'journalModifiedDate',
      'journal_modified_date',
      'uploadDate',
      'createdAt',
      'created_at',
      'transactionDate',
      'journalDate',
    ]) ?? null
  );
}

function getEventDateLabel(
  row: Record<string, unknown>,
  rowsByUuid?: Map<string, Record<string, unknown>>
): string | null {
  if (isDeletedHistoryRow(row)) {
    return formatJournalModifiedDateForDisplay(getDeletedDateRaw(row));
  }

  // REVERSAL_LINE usually has no journalModifiedDate — use the reversed row's.
  if (getHistoryReversalType(row) === 'REVERSAL_LINE' && rowsByUuid) {
    const parent = rowsByUuid.get(getLinkedTransactionId(row));
    if (parent) {
      const parentDate = formatJournalModifiedDateForDisplay(
        getEditedDateRaw(parent)
      );
      if (parentDate) return parentDate;
    }
  }

  const ownEditDate = formatJournalModifiedDateForDisplay(getEditedDateRaw(row));
  if (ownEditDate) return ownEditDate;

  // Current / replacement rows often have null journalModifiedDate. The edit
  // that created them is on the superseded version they link from — never use
  // transactionDate here (that is the invoice date, not the edit date).
  if (rowsByUuid) {
    let prevId = getLinkedTransactionId(row);
    const seen = new Set<string>();
    while (prevId && !seen.has(prevId)) {
      seen.add(prevId);
      const prev = rowsByUuid.get(prevId);
      if (!prev) break;
      const fromPrev = formatJournalModifiedDateForDisplay(
        getEditedDateRaw(prev)
      );
      if (fromPrev) return fromPrev;
      prevId = getLinkedTransactionId(prev);
    }
  }

  return null;
}

const USER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getUserDisplayName(user: User): string | null {
  const fullName = user.fullName?.trim();
  if (fullName) return fullName;
  const combined = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  if (combined) return combined;
  const email = user.email?.trim();
  return email || null;
}

function findUserInList(
  users: User[] | undefined,
  opts: { id?: string | null; email?: string | null }
): User | undefined {
  if (!users?.length) return undefined;
  const id = opts.id?.trim().toLowerCase();
  const email = opts.email?.trim().toLowerCase();
  return users.find((user) => {
    if (id) {
      if (user.userUuid?.toLowerCase() === id) return true;
      if (user.accountUuid?.toLowerCase() === id) return true;
    }
    if (email && user.email?.toLowerCase() === email) return true;
    return false;
  });
}

function resolveUserIdToName(
  value: unknown,
  users: User[] | undefined
): string | null {
  if (typeof value !== 'string') return null;
  const id = value.trim();
  if (!id) return null;
  const matched = findUserInList(users, { id });
  return matched ? getUserDisplayName(matched) : null;
}

/** Resolve display name from history row fields / account users list. */
function getHistoryActorName(
  row: Record<string, unknown>,
  users?: User[]
): string | null {
  const fromString = (value: unknown): string | null => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  };

  /** Prefer human-readable strings; skip raw user UUIDs. */
  const fromNonIdString = (value: unknown): string | null => {
    const text = fromString(value);
    if (!text || USER_ID_RE.test(text)) return null;
    return text;
  };

  const fromUserObject = (value: unknown): string | null => {
    if (!value || typeof value !== 'object') return null;
    const user = value as Record<string, unknown>;

    const id =
      fromString(user.userUuid) ??
      fromString(user.accountUuid) ??
      fromString(user.id);
    const email = fromString(user.email);
    const matched = findUserInList(users, { id, email });
    if (matched) {
      return getUserDisplayName(matched);
    }

    const fullName = fromString(user.fullName) ?? fromString(user.userFullName);
    if (fullName) return fullName;
    const first = fromString(user.firstName) ?? '';
    const last = fromString(user.lastName) ?? '';
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;
    return email ?? fromString(user.name);
  };

  return (
    fromString(row.updatedByName) ??
    fromString(row.editedByName) ??
    fromString(row.modifiedByName) ??
    fromString(row.userFullName) ??
    fromUserObject(row.modifiedByUser) ??
    fromUserObject(row.updatedByUser) ??
    fromUserObject(row.editedByUser) ??
    resolveUserIdToName(row.modifiedBy, users) ??
    resolveUserIdToName(row.updatedBy, users) ??
    resolveUserIdToName(row.editedBy, users) ??
    resolveUserIdToName(row.userUuid, users) ??
    resolveUserIdToName(row.userId, users) ??
    fromUserObject(row.updatedBy) ??
    fromUserObject(row.editedBy) ??
    fromUserObject(row.modifiedBy) ??
    fromUserObject(row.user) ??
    fromUserObject(row.userDto) ??
    fromNonIdString(row.updatedBy) ??
    fromNonIdString(row.editedBy) ??
    fromNonIdString(row.modifiedBy)
  );
}

function parseComparableNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(num) ? num : null;
}

function normalizeDisplayValue(
  key: string,
  raw: unknown,
  row: Record<string, unknown>,
  t: (key: string) => string
): string {
  if (raw == null || (typeof raw === 'string' && raw.trim() === '')) {
    return '—';
  }
  if (
    key === 'transactionDate' ||
    key === 'dueDate' ||
    key === 'uploadDate' ||
    key === 'journalDate'
  ) {
    return dayjs(raw as string | number).isValid()
      ? dayjs(raw as string | number).format('DD/MM/YYYY')
      : String(raw);
  }
  if (key === 'taxRate') {
    return `${raw}%`;
  }
  if (
    NUMERIC_COLUMN_KEYS.has(key) ||
    key === 'netAmountConverted' ||
    key === 'taxAmountConverted' ||
    key === 'grossAmountConverted'
  ) {
    const num = parseComparableNumber(raw);
    if (num == null) return String(raw);
    if (key === 'quantity') return formatTransactionQuantityDisplay(num);
    return formatTransactionAmountDisplay(num);
  }
  if (key === 'sourceType') {
    return getSourceTypeDisplayLabel(String(raw), t);
  }
  if (key === 'accountCode') {
    const accountName = row.accountName;
    return accountName ? `${raw} — ${accountName}` : String(raw);
  }
  return String(raw);
}

function getFieldRaw(row: Record<string, unknown>, key: string): unknown {
  if (key === 'gross') {
    return pickHistoryField(row, ['gross', 'grossAmountConverted']);
  }
  if (key === 'net') {
    return pickHistoryField(row, ['net', 'netAmountConverted']);
  }
  if (key === 'tax') {
    return pickHistoryField(row, ['tax', 'taxAmountConverted']);
  }
  if (key === 'transactionDate') {
    return pickHistoryField(row, ['transactionDate', 'journalDate']);
  }
  return row[key];
}

function buildFieldChanges(
  previous: Record<string, unknown> | null,
  current: Record<string, unknown>,
  t: (key: string) => string
): FieldChange[] {
  if (!previous) return [];
  const seenLabels = new Set<string>();
  const changes: FieldChange[] = [];

  for (const key of HISTORY_COMPARE_FIELDS) {
    // Prefer canonical excel keys; skip journal aliases when canonical already compared.
    if (
      (key === 'journalDate' && previous.transactionDate != null) ||
      (key === 'grossAmountConverted' && previous.gross != null) ||
      (key === 'netAmountConverted' && previous.net != null) ||
      (key === 'taxAmountConverted' && previous.tax != null)
    ) {
      continue;
    }

    const fromRaw = getFieldRaw(previous, key);
    const toRaw = getFieldRaw(current, key);
    const from = normalizeDisplayValue(key, fromRaw, previous, t);
    const to = normalizeDisplayValue(key, toRaw, current, t);
    if (from === to) continue;

    const labelKey =
      key === 'grossAmountConverted'
        ? 'gross'
        : key === 'netAmountConverted'
          ? 'net'
          : key === 'taxAmountConverted'
            ? 'tax'
            : key === 'journalDate'
              ? 'transactionDate'
              : key;
    const label = getEntityTransactionColumnTitle(labelKey, t);
    if (seenLabels.has(label)) continue;
    seenLabels.add(label);
    changes.push({ key, label, from, to });
  }

  return changes;
}

function getSortDateValue(row: HistoryRow): number {
  const raw = getSortDateRaw(getHistoryRowData(row));
  const d = dayjs(raw as string | number);
  return d.isValid() ? d.valueOf() : 0;
}

function isVersionHistoryRow(data: Record<string, unknown>): boolean {
  return getHistoryReversalType(data) !== 'REVERSAL_LINE';
}

function compareVersionSiblings(a: HistoryRow, b: HistoryRow): number {
  const byDate = getSortDateValue(a) - getSortDateValue(b);
  if (byDate !== 0) return byDate;
  const typeA = getHistoryReversalType(getHistoryRowData(a));
  const typeB = getHistoryReversalType(getHistoryRowData(b));
  // Soft-delete is always the terminal version among siblings.
  if (typeA === 'DELETED' && typeB !== 'DELETED') return 1;
  if (typeB === 'DELETED' && typeA !== 'DELETED') return -1;
  // Live current row (NONE) comes after superseded versions (REVERSED).
  if (typeA === 'NONE' && typeB !== 'NONE') return 1;
  if (typeB === 'NONE' && typeA !== 'NONE') return -1;
  return String(a.key).localeCompare(String(b.key));
}

/**
 * Order/classify from BE fields:
 * - REVERSED / NONE / DELETED = succession versions (linkedTransactionId → prior version)
 * - REVERSAL_LINE = journal that reverses a REVERSED/DELETED row (linkedTransactionId → that uuid)
 *
 * Edit sequence: Original → Reversal → Updated → … → Current
 * Delete-only (DELETED + REVERSAL_LINE, no prior version): Original → Deleted
 *   (Original amounts are recovered from the cancelling line / negated DELETED amounts;
 *    the REVERSAL_LINE after DELETED is accounting plumbing and is not shown as its own step.)
 */
function negateHistoryAmountFields(
  row: Record<string, unknown>
): Record<string, unknown> {
  const next = { ...row };
  for (const key of [
    'gross',
    'net',
    'tax',
    'grossAmountConverted',
    'netAmountConverted',
    'taxAmountConverted',
    'discountAmount',
  ] as const) {
    const num = parseComparableNumber(next[key]);
    if (num != null) next[key] = -num;
  }
  return next;
}

/** Pre-delete snapshot when history only has DELETED (+ its REVERSAL_LINE). */
function buildOriginalSnapshotFromDeleted(
  deleted: Record<string, unknown>,
  reversal: Record<string, unknown> | undefined
): Record<string, unknown> {
  const amountKeys = [
    'gross',
    'net',
    'tax',
    'grossAmountConverted',
    'netAmountConverted',
    'taxAmountConverted',
    'discountAmount',
  ] as const;

  let snapshot: Record<string, unknown>;
  if (reversal) {
    snapshot = { ...deleted };
    for (const key of amountKeys) {
      if (reversal[key] != null && String(reversal[key]).trim() !== '') {
        snapshot[key] = reversal[key];
      }
    }
  } else {
    snapshot = negateHistoryAmountFields(deleted);
  }

  return {
    ...snapshot,
    reversalType: 'NONE',
    linkedTransactionId: null,
    journalModifiedDate: null,
    // Keep a stable synthetic id for React keys / maps.
    uuid: `${getRowUuid(deleted) || 'deleted'}-original`,
  };
}

function classifyHistoryRows(rows: HistoryRow[]): Array<{
  row: HistoryRow;
  data: Record<string, unknown>;
  kind: HistoryEventKind;
}> {
  if (rows.length === 0) return [];

  const rowsByUuid = new Map<string, HistoryRow>();
  rows.forEach((row) => {
    const id = getRowUuid(getHistoryRowData(row));
    if (id) rowsByUuid.set(id, row);
  });

  const versionRows = rows.filter((row) =>
    isVersionHistoryRow(getHistoryRowData(row))
  );
  const reversalByTargetId = new Map<string, HistoryRow>();
  rows.forEach((row) => {
    const data = getHistoryRowData(row);
    if (getHistoryReversalType(data) !== 'REVERSAL_LINE') return;
    const targetId = getLinkedTransactionId(data);
    if (targetId) reversalByTargetId.set(targetId, row);
  });

  const nextVersionsByPrevId = new Map<string, HistoryRow[]>();
  const roots: HistoryRow[] = [];

  versionRows.forEach((row) => {
    const data = getHistoryRowData(row);
    const prevId = getLinkedTransactionId(data);
    if (!prevId || !rowsByUuid.has(prevId)) {
      roots.push(row);
      return;
    }
    const list = nextVersionsByPrevId.get(prevId) ?? [];
    list.push(row);
    nextVersionsByPrevId.set(prevId, list);
  });

  roots.sort(compareVersionSiblings);

  const orderedVersions: HistoryRow[] = [];
  const visited = new Set<string>();

  const walk = (row: HistoryRow) => {
    const id = getRowUuid(getHistoryRowData(row));
    if (id && visited.has(id)) return;
    if (id) visited.add(id);
    orderedVersions.push(row);
    const nexts = [...(nextVersionsByPrevId.get(id) ?? [])].sort(
      compareVersionSiblings
    );
    nexts.forEach(walk);
  };

  if (roots.length > 0) {
    roots.forEach(walk);
  } else if (versionRows.length > 0) {
    [...versionRows].sort(compareVersionSiblings).forEach(walk);
  }

  versionRows.forEach((row) => {
    const id = getRowUuid(getHistoryRowData(row));
    if (!id || visited.has(id)) return;
    walk(row);
  });

  const classified: Array<{
    row: HistoryRow;
    data: Record<string, unknown>;
    kind: HistoryEventKind;
  }> = [];
  const emitted = new Set<string>();

  orderedVersions.forEach((versionRow, index) => {
    const data = getHistoryRowData(versionRow);
    const uuid = getRowUuid(data);
    if (uuid) emitted.add(uuid);

    const reversalRow = uuid ? reversalByTargetId.get(uuid) : undefined;
    const reversalData = reversalRow
      ? getHistoryRowData(reversalRow)
      : undefined;

    if (isDeletedHistoryRow(data)) {
      // Delete with no prior version in this history payload: recover Original.
      if (index === 0) {
        const originalData = buildOriginalSnapshotFromDeleted(
          data,
          reversalData
        );
        const originalRow: HistoryRow = {
          ...versionRow,
          key: `${versionRow.key}-original`,
          __transactionRow: originalData,
        };
        classified.push({
          row: originalRow,
          data: originalData,
          kind: 'original',
        });
      }

      // Show the deleted event with pre-delete amounts so facts match Original.
      const deletedDisplay = buildOriginalSnapshotFromDeleted(
        data,
        reversalData
      );
      // Preserve delete metadata for dates / badge while showing live amounts.
      deletedDisplay.reversalType = 'DELETED';
      deletedDisplay.journalModifiedDate = data.journalModifiedDate;
      deletedDisplay.uuid = data.uuid;
      deletedDisplay.linkedTransactionId = data.linkedTransactionId;

      classified.push({
        row: {
          ...versionRow,
          __transactionRow: deletedDisplay,
        },
        data: deletedDisplay,
        kind: 'deleted',
      });

      // Mark cancelling line consumed — not shown after DELETED (unlike REVERSED).
      if (reversalData) {
        const reversalId = getRowUuid(reversalData);
        if (reversalId) emitted.add(reversalId);
      }
      return;
    }

    let kind: HistoryEventKind;
    if (index === 0) {
      kind = 'original';
    } else {
      kind = 'updated';
    }

    classified.push({ row: versionRow, data, kind });

    // Pair reversing journal lines with REVERSED versions only.
    if (reversalRow && getHistoryReversalType(data) === 'REVERSED') {
      const reversalId = getRowUuid(reversalData!);
      if (reversalId) emitted.add(reversalId);
      classified.push({
        row: reversalRow,
        data: reversalData!,
        kind: 'reversal',
      });
    }
  });

  // Any leftover REVERSAL_LINE not paired above (still show them).
  rows.forEach((row) => {
    const data = getHistoryRowData(row);
    const id = getRowUuid(data);
    if (id && emitted.has(id)) return;
    if (getHistoryReversalType(data) === 'REVERSAL_LINE') {
      classified.push({ row, data, kind: 'reversal' });
      if (id) emitted.add(id);
      return;
    }
    classified.push({
      row,
      data,
      kind: isDeletedHistoryRow(data)
        ? 'deleted'
        : classified.length === 0
          ? 'original'
          : 'updated',
    });
    if (id) emitted.add(id);
  });

  return classified;
}

function buildTimelineEntries(
  rows: HistoryRow[],
  t: (key: string) => string,
  users?: User[]
): TimelineEntry[] {
  const classified = classifyHistoryRows(rows);
  const rowsByUuid = new Map<string, Record<string, unknown>>();
  classified.forEach((entry) => {
    const id = getRowUuid(entry.data);
    if (id) rowsByUuid.set(id, entry.data);
  });

  const currentIndex = (() => {
    // Soft-deleted history has no live "current" version.
    if (classified.some((entry) => entry.kind === 'deleted')) {
      return -1;
    }
    // Only an explicit NONE row is current — never fall back to Original.
    return classified.findIndex(
      (entry) =>
        (entry.kind === 'original' || entry.kind === 'updated') &&
        getHistoryReversalType(entry.data) === 'NONE'
    );
  })();

  let previousForwardForDiff: Record<string, unknown> | null = null;
  let previousAny: Record<string, unknown> | null = null;

  return classified.map((entry, index) => {
    const isCurrent = index === currentIndex;
    // Diff versions against prior version; reversals against the version they negate.
    const diffBase =
      entry.kind === 'reversal'
        ? rowsByUuid.get(getLinkedTransactionId(entry.data)) ??
          previousForwardForDiff
        : entry.kind === 'deleted'
          ? previousAny
          : previousForwardForDiff;

    const changes = buildFieldChanges(diffBase, entry.data, t);

    previousAny = entry.data;
    if (entry.kind === 'original' || entry.kind === 'updated') {
      previousForwardForDiff = entry.data;
    }

    return {
      ...entry,
      isCurrent,
      step: index + 1,
      eventDateLabel: getEventDateLabel(entry.data, rowsByUuid),
      actorName: getHistoryActorName(entry.data, users),
      changes,
    };
  });
}

function entryMatchesSearch(entry: TimelineEntry, query: string): boolean {
  if (!query) return true;
  const haystack = [
    entry.kind,
    entry.eventDateLabel ?? '',
    entry.actorName ?? '',
    ...entry.changes.flatMap((change) => [
      change.label,
      change.from,
      change.to,
    ]),
    ...HISTORY_COMPARE_FIELDS.map((key) =>
      String(getFieldRaw(entry.data, key) ?? '')
    ),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

export const TransactionHistoryModalContent: React.FC<{
  transactionUuid: string;
  applicationConnectionId: string;
  providerName?: string;
  organisationId?: string;
}> = ({ transactionUuid, applicationConnectionId }) => {
  const { t } = useTranslation();
  const accountUsers = useSelector(getUserMembers);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<HistoryRow[]>([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(
    () => new Set()
  );
  const [historyView, setHistoryView] = React.useState<'timeline' | 'table'>(
    'timeline'
  );
  const [historyTablePageSize, setHistoryTablePageSize] = React.useState(20);

  React.useEffect(() => {
    const uuid = transactionUuid?.trim() ?? '';
    const connectionId = applicationConnectionId?.trim() ?? '';
    let cancelled = false;

    if (!uuid || !connectionId) {
      setLoading(false);
      setRows([]);
      setError(t('vat-report.no-activity-logs-available'));
      return;
    }

    setLoading(true);
    setError(null);
    setSearchQuery('');
    setExpandedKeys(new Set());
    setHistoryView('timeline');

    void getTransactionHistory(uuid, connectionId)
      .then((response) => {
        if (cancelled) return;
        const list = extractHistoryList(response?.data);
        setRows(
          list.map((item, index) => {
            const id =
              String(
                item.uuid ??
                  item.journalId ??
                  item.id ??
                  item.createdAt ??
                  index
              ).trim() || String(index);
            return {
              ...item,
              key: id,
              __transactionRow: item,
              __transactionUuid: item.uuid ?? id,
            };
          })
        );
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          e instanceof Error && e.message
            ? e.message
            : t('vat-report.no-activity-logs-available')
        );
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionUuid, applicationConnectionId]);

  const timeline = React.useMemo(
    () => buildTimelineEntries(rows, t, accountUsers),
    [accountUsers, rows, t]
  );

  const filteredTimeline = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return timeline.filter((entry) => entryMatchesSearch(entry, query));
  }, [searchQuery, timeline]);

  React.useEffect(() => {
    // Keep cards collapsed by default so the modal fits short viewports;
    // users can expand "What changed" per step.
    setExpandedKeys(new Set());
  }, [timeline]);

  const latestSummary = React.useMemo(() => {
    const deleted = [...timeline].reverse().find((e) => e.kind === 'deleted');
    if (deleted?.eventDateLabel) {
      return {
        label: t('organisation.transaction-deleted-date'),
        date: deleted.eventDateLabel,
        actorName: deleted.actorName,
      };
    }

    // Prefer the newest real edit date (never transactionDate — that is handled
    // in getEventDateLabel). Walk newest-first among updated/current, then any
    // reversed original that carries journalModifiedDate.
    const latest = [...timeline]
      .reverse()
      .find(
        (e) =>
          !!e.eventDateLabel &&
          (e.isCurrent || e.kind === 'updated' || e.kind === 'original')
      );
    if (latest?.eventDateLabel) {
      return {
        label: t('organisation.transaction-history-last-edited'),
        date: latest.eventDateLabel,
        actorName: latest.actorName,
      };
    }
    return null;
  }, [t, timeline]);

  const editCount = React.useMemo(
    () => timeline.filter((entry) => entry.kind === 'updated').length,
    [timeline]
  );

  const eventLabel = React.useCallback(
    (kind: HistoryEventKind, isCurrent: boolean) => {
      if (isCurrent && kind !== 'deleted' && kind !== 'reversal') {
        return t('organisation.transaction-history-event-current');
      }
      switch (kind) {
        case 'original':
          return t('organisation.transaction-history-event-original');
        case 'reversal':
          return t('organisation.transaction-history-event-reversal');
        case 'updated':
          return t('organisation.transaction-history-event-updated');
        case 'deleted':
          return t('organisation.transaction-deleted-badge');
        case 'current':
          return t('organisation.transaction-history-event-current');
        default:
          return kind;
      }
    },
    [t]
  );

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderSummaryFacts = (data: Record<string, unknown>) => {
    const facts: Array<{ label: string; value: string }> = [];
    const seen = new Set<string>();

    for (const key of HISTORY_SUMMARY_FIELDS) {
      const canonical =
        key === 'journalDate'
          ? 'transactionDate'
          : key === 'grossAmountConverted'
            ? 'gross'
            : key === 'netAmountConverted'
              ? 'net'
              : key === 'taxAmountConverted'
                ? 'tax'
                : key;
      if (seen.has(canonical)) continue;
      const raw = getFieldRaw(data, key);
      if (raw == null || (typeof raw === 'string' && raw.trim() === '')) {
        continue;
      }
      seen.add(canonical);
      facts.push({
        label: getEntityTransactionColumnTitle(canonical, t),
        value: normalizeDisplayValue(key, raw, data, t),
      });
      if (facts.length >= 6) break;
    }

    if (!facts.length) return null;

    return (
      <HistorySummaryFacts>
        {facts.map((fact) => (
          <HistorySummaryFact key={fact.label}>
            <span className="fact-label">{fact.label}</span>
            <span className="fact-value">{fact.value}</span>
          </HistorySummaryFact>
        ))}
      </HistorySummaryFacts>
    );
  };

  const formatFactValue = React.useCallback(
    (data: Record<string, unknown>, key: string) => {
      const raw = getFieldRaw(data, key);
      if (raw == null || (typeof raw === 'string' && raw.trim() === '')) {
        return '—';
      }
      return normalizeDisplayValue(key, raw, data, t);
    },
    [t]
  );

  const historyTableData = React.useMemo(
    () =>
      filteredTimeline.map((entry) => ({
        key: entry.row.key,
        step: entry.step,
        kind: entry.kind,
        isCurrent: entry.isCurrent,
        eventDateLabel: entry.eventDateLabel,
        actorName: entry.actorName,
        __historyEntry: entry,
      })),
    [filteredTimeline]
  );

  const historyTableColumns = React.useMemo(
    () => [
      {
        title: t('organisation.transaction-history-step-column'),
        key: 'step',
        dataIndex: 'step',
        width: 72,
        sorter: false,
      },
      {
        title: t('organisation.transaction-status-column'),
        key: 'status',
        dataIndex: 'status',
        width: 110,
        sorter: false,
        render: (_: unknown, record: (typeof historyTableData)[number]) => {
          const badgeKind = record.isCurrent ? 'current' : record.kind;
          return (
            <HistoryEventBadge $kind={badgeKind}>
              {eventLabel(record.kind, record.isCurrent)}
            </HistoryEventBadge>
          );
        },
      },
      {
        title: t('organisation.transaction-history-event-date-column'),
        key: 'eventDateLabel',
        dataIndex: 'eventDateLabel',
        width: 120,
        sorter: false,
        render: (value: string | null) => value ?? '—',
      },
      {
        title: t('organisation.transaction-history-updated-by-column'),
        key: 'actorName',
        dataIndex: 'actorName',
        width: 140,
        sorter: false,
        render: (value: string | null) => value ?? '—',
      },
      {
        title: getEntityTransactionColumnTitle('transactionDate', t),
        key: 'transactionDate',
        dataIndex: 'transactionDate',
        width: 110,
        sorter: false,
        render: (_: unknown, record: (typeof historyTableData)[number]) =>
          formatFactValue(record.__historyEntry.data, 'transactionDate'),
      },
      {
        title: getEntityTransactionColumnTitle('sourceType', t),
        key: 'sourceType',
        dataIndex: 'sourceType',
        width: 160,
        sorter: false,
        render: (_: unknown, record: (typeof historyTableData)[number]) =>
          formatFactValue(record.__historyEntry.data, 'sourceType'),
      },
      {
        title: getEntityTransactionColumnTitle('transactionNumber', t),
        key: 'transactionNumber',
        dataIndex: 'transactionNumber',
        width: 110,
        sorter: false,
        render: (_: unknown, record: (typeof historyTableData)[number]) =>
          formatFactValue(record.__historyEntry.data, 'transactionNumber'),
      },
      {
        title: getEntityTransactionColumnTitle('currency', t),
        key: 'currency',
        dataIndex: 'currency',
        width: 80,
        sorter: false,
        render: (_: unknown, record: (typeof historyTableData)[number]) =>
          formatFactValue(record.__historyEntry.data, 'currency'),
      },
      {
        title: getEntityTransactionColumnTitle('gross', t),
        key: 'gross',
        dataIndex: 'gross',
        width: 100,
        sorter: false,
        render: (_: unknown, record: (typeof historyTableData)[number]) =>
          formatFactValue(record.__historyEntry.data, 'gross'),
      },
      {
        title: getEntityTransactionColumnTitle('net', t),
        key: 'net',
        dataIndex: 'net',
        width: 100,
        sorter: false,
        render: (_: unknown, record: (typeof historyTableData)[number]) =>
          formatFactValue(record.__historyEntry.data, 'net'),
      },
    ],
    [eventLabel, formatFactValue, historyTableData, t]
  );

  return (
    <Spin spinning={loading}>
      <HistoryToolbar>
        <HistoryMetaLine>
          {!loading && rows.length > 0 ? (
            <>
              <span>
                {t('organisation.transaction-history-version-count', {
                  count: rows.length,
                })}
              </span>
              {editCount > 0 ? (
                <span>
                  {t('organisation.transaction-history-edit-count', {
                    count: editCount,
                  })}
                </span>
              ) : null}
              {latestSummary ? (
                <span>
                  <strong>{latestSummary.label}:</strong> {latestSummary.date}
                  {latestSummary.actorName
                    ? ` · ${t('organisation.transaction-history-by-user', {
                        name: latestSummary.actorName,
                      })}`
                    : null}
                </span>
              ) : null}
            </>
          ) : (
            <span />
          )}
        </HistoryMetaLine>
        <HistorySearchRow>
          <HistoryViewToggle>
            <Segmented<'timeline' | 'table'>
              size="small"
              value={historyView}
              aria-label={t('organisation.transaction-history-view-label')}
              options={[
                {
                  label: t('organisation.transaction-history-view-timeline'),
                  value: 'timeline',
                  icon: <FieldTimeOutlined />,
                },
                {
                  label: t('organisation.transaction-history-view-table'),
                  value: 'table',
                  icon: <TableOutlined />,
                },
              ]}
              onChange={(value) => setHistoryView(value)}
            />
          </HistoryViewToggle>
          <RoundInput
            width="200px"
            height="35px"
            prefix={<SearchOutlined />}
            size="large"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t(
              'organisation.transaction-history-search-placeholder'
            )}
            aria-label={t(
              'organisation.transaction-history-search-placeholder'
            )}
          />
        </HistorySearchRow>
      </HistoryToolbar>

      {error ? (
        <HistoryEmptyState role="status">{error}</HistoryEmptyState>
      ) : !loading && !rows.length ? (
        <HistoryEmptyState role="status">
          {t('vat-report.no-activity-logs-available')}
        </HistoryEmptyState>
      ) : !loading && !filteredTimeline.length ? (
        <HistoryEmptyState role="status">
          {t('organisation.transaction-history-empty-search')}
        </HistoryEmptyState>
      ) : historyView === 'table' ? (
        <HistoryTableWrap>
          <ScopeTable
            className="transaction-table validation-errors-table"
            tableDataSource={historyTableData}
            originalDataSource={historyTableData}
            columns={historyTableColumns}
            pagination={true}
            rowsPerPage={historyTablePageSize}
            enableColumnsCustomization={false}
            rowHeight={40}
            showSizeChanger
            getNewPageSize={setHistoryTablePageSize}
            scroll={{ x: 'max-content' }}
            fontSize={12}
            reportId=""
            transactionType=""
            haveBorder
            rowKey="key"
          />
        </HistoryTableWrap>
      ) : (
        <HistoryTimeline aria-label={t('common.view-history')}>
          {filteredTimeline.map((entry) => {
            const badgeKind = entry.isCurrent ? 'current' : entry.kind;
            const expanded = expandedKeys.has(entry.row.key);
            const hasChanges = entry.changes.length > 0;
            const showDetails = expanded && hasChanges;

            return (
              <HistoryTimelineItem key={entry.row.key}>
                <HistoryTimelineRail>
                  <HistoryTimelineMarker $kind={badgeKind} aria-hidden />
                </HistoryTimelineRail>
                <HistoryTimelineCard $kind={badgeKind}>
                  <HistoryTimelineHeader>
                    <div className="header-main">
                      <HistoryEventBadge $kind={badgeKind}>
                        {eventLabel(entry.kind, entry.isCurrent)}
                      </HistoryEventBadge>
                      <span className="version-label">
                        {t('organisation.transaction-history-step-label', {
                          n: entry.step,
                        })}
                      </span>
                      {entry.eventDateLabel || entry.actorName ? (
                        <span className="event-meta">
                          {entry.eventDateLabel ? (
                            <time dateTime={entry.eventDateLabel}>
                              {entry.kind === 'updated' || entry.isCurrent
                                ? t(
                                    'organisation.transaction-history-edited-on',
                                    { date: entry.eventDateLabel }
                                  )
                                : entry.kind === 'deleted'
                                  ? t(
                                      'organisation.transaction-history-deleted-on',
                                      { date: entry.eventDateLabel }
                                    )
                                  : entry.eventDateLabel}
                            </time>
                          ) : null}
                          {entry.actorName ? (
                            <span className="event-actor">
                              {t('organisation.transaction-history-by-user', {
                                name: entry.actorName,
                              })}
                            </span>
                          ) : null}
                        </span>
                      ) : null}
                    </div>
                    {hasChanges ? (
                      <button
                        type="button"
                        className="details-toggle"
                        aria-expanded={expanded}
                        onClick={() => toggleExpanded(entry.row.key)}
                      >
                        {expanded
                          ? t('organisation.transaction-history-hide-changes')
                          : t('organisation.transaction-history-show-changes')}
                      </button>
                    ) : null}
                  </HistoryTimelineHeader>

                  {entry.kind === 'reversal' ? (
                    <p className="event-hint">
                      {t('organisation.transaction-history-reversal-hint')}
                    </p>
                  ) : null}
                  {entry.kind === 'deleted' ? (
                    <p className="event-hint">
                      {t('organisation.transaction-deleted-badge-tooltip')}
                    </p>
                  ) : null}

                  {renderSummaryFacts(entry.data)}

                  {showDetails ? (
                    <HistoryChangeList
                      aria-label={t(
                        'organisation.transaction-history-changes'
                      )}
                    >
                      <div className="changes-title">
                        {t('organisation.transaction-history-changes')}
                      </div>
                      {entry.changes.map((change) => (
                        <HistoryChangeRow key={change.key}>
                          <span className="change-label">{change.label}</span>
                          <span className="change-values">
                            <HistoryChangeFrom>{change.from}</HistoryChangeFrom>
                            <HistoryChangeArrow aria-hidden>
                              →
                            </HistoryChangeArrow>
                            <HistoryChangeTo>{change.to}</HistoryChangeTo>
                          </span>
                        </HistoryChangeRow>
                      ))}
                    </HistoryChangeList>
                  ) : null}

                  {!hasChanges && entry.step > 1 ? (
                    <p className="no-changes">
                      {t('organisation.transaction-history-no-changes')}
                    </p>
                  ) : null}
                </HistoryTimelineCard>
              </HistoryTimelineItem>
            );
          })}
        </HistoryTimeline>
      )}
    </Spin>
  );
};
